/**
 * The composer's formatting toolbar — option A (owner, 2026-10-01).
 *
 * Every button edits the text box: it wraps the selection in marks or prefixes
 * the lines it touches (`lib/community-format.ts` `wrap` / `prefixLines`), then
 * puts the selection back on what it wrapped, so formatting twice in a row is
 * two clicks rather than a re-selection. Nothing here renders a post — Preview
 * uses the same `PostText` readers see.
 */

import { useState, type RefObject } from "react";

import { COLOUR_VAR, POST_COLOURS, prefixLines, wrap, type Edit } from "../../lib/community-format";

export function FormatBar({
  textRef,
  value,
  onChange,
}: {
  textRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (text: string) => void;
}) {
  const [menu, setMenu] = useState<null | "heading" | "colour">(null);

  const apply = (edit: (text: string, start: number, end: number) => Edit) => {
    const el = textRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = edit(value, start, end);
    onChange(next.text);
    setMenu(null);
    // After React writes the new value, select what was formatted.
    requestAnimationFrame(() => {
      const box = textRef.current;
      if (!box) return;
      box.focus();
      box.setSelectionRange(next.selStart, next.selEnd);
    });
  };

  const inline = (open: string, close: string, placeholder?: string) => () =>
    apply((t, s, e) => wrap(t, s, e, open, close, placeholder));
  const lines = (prefix: (n: number) => string) => () => apply((t, s, e) => prefixLines(t, s, e, prefix));

  return (
    <div style={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", margin: "2px 0 3px" }} role="toolbar" aria-label="Formatting">
      <Key title="Bold" onClick={inline("**", "**")}><b>B</b></Key>
      <Key title="Italic" onClick={inline("*", "*")}><i>I</i></Key>
      <span style={{ position: "relative" }}>
        <Key title="Heading size" onClick={() => setMenu(menu === "heading" ? null : "heading")}>H▾</Key>
        {menu === "heading" && (
          <Menu>
            {([1, 2, 3] as const).map((level) => (
              <Item key={level} onClick={lines(() => `${"#".repeat(level)} `)}>
                <span style={{ fontWeight: 700, fontSize: { 1: 16, 2: 14, 3: 12.5 }[level] }}>
                  {{ 1: "Big heading", 2: "Medium heading", 3: "Small heading" }[level]}
                </span>
              </Item>
            ))}
          </Menu>
        )}
      </span>
      <span style={{ position: "relative" }}>
        <Key title="Colour" onClick={() => setMenu(menu === "colour" ? null : "colour")}>
          <span style={{ borderBottom: "3px solid var(--qf-positive)", lineHeight: 1 }}>A</span>▾
        </Key>
        {menu === "colour" && (
          <Menu>
            {POST_COLOURS.map((c) => (
              <Item key={c} onClick={inline(`{${c}}`, `{/${c}}`)}>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: COLOUR_VAR[c], marginRight: 6 }} />
                <span style={{ color: COLOUR_VAR[c] }}>{c}</span>
              </Item>
            ))}
          </Menu>
        )}
      </span>
      <Key title="Small grey text — for notes" onClick={inline("{small}", "{/small}", "note")}><span style={{ fontSize: 10 }}>small</span></Key>
      <Sep />
      <Key title="Bulleted list" onClick={lines(() => "- ")}>•≡</Key>
      <Key title="Numbered list" onClick={lines((n) => `${n + 1}. `)}>1≡</Key>
      <Key title="Quote" onClick={lines(() => "> ")}>❝</Key>
      <Key
        title="Link"
        onClick={() => {
          const url = window.prompt("Link to (https://…)", "https://");
          if (!url || !/^https?:\/\/\S+$/i.test(url)) return;
          apply((t, s, e) => wrap(t, s, e, "[", `](${url})`, "link text"));
        }}
      >
        🔗
      </Key>
    </div>
  );
}

function Key({ children, title, onClick }: { children: React.ReactNode; title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{
        minWidth: 26,
        height: 24,
        padding: "0 5px",
        border: "1px solid transparent",
        borderRadius: 4,
        background: "none",
        color: "var(--qf-text)",
        cursor: "pointer",
        fontSize: 12.5,
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span aria-hidden style={{ width: 1, height: 16, background: "var(--qf-border)", margin: "0 3px" }} />;
}

function Menu({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="menu"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        zIndex: 30,
        marginTop: 2,
        minWidth: 140,
        padding: 3,
        background: "var(--qf-surface)",
        border: "1px solid var(--qf-border)",
        borderRadius: 5,
      }}
    >
      {children}
    </div>
  );
}

function Item({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{ display: "flex", alignItems: "center", width: "100%", textAlign: "left", border: 0, background: "none", color: "var(--qf-text)", padding: "4px 8px", fontSize: 12, cursor: "pointer" }}
    >
      {children}
    </button>
  );
}
